import moment from "moment"
import React from "react"
import { Col, Dropdown, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import { PickModel } from "../../api/pick.api"
import { useAppDispatch } from "../../app/hooks"
import ChargeableWeight from "../../components/Tables/ChargeableWeight"
import { SearchBar } from "../../components/Tables/Columns"
// import { logoUrl } from "config/config";
import CustomTotal from "../../components/Tables/CustomTotal"
import GetVolumeWeight from "../../components/Tables/GetVolumeWeight"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { setScannedSuite } from "../../store/pick.store"

function PickSuite({
    setShow,
    picks,
    setButtonId
}: {
    setShow: Function
    picks: PickModel[]
    setButtonId: Function
}) {
    const { t } = useTranslation()
    setButtonId("#pickExportButton")
    const dispatch = useAppDispatch()

    const handleAction = (action: string, scannedSuite: Record<string, any>) => {
        switch (action) {
            case "Scan Suite":
                dispatch(setScannedSuite(scannedSuite))
                setShow(true)
                break
            default:
                break
        }
    }

    const Columns = [
        {
            dataField: "created_at",
            text: t("Date"),
            sort: true,
            style: {
                width: 245
            },
            formatter: (cell: number, row: Record<string, any>) => {
                if (cell) return moment(cell).format("DD MMM yyyy")
            }
        },
        {
            dataField: "suite",
            text: t("Suite_Id"),
            style: {
                width: 245,
                color: "#1E88E5"
            }
        },
        {
            text: t("Parcels_to_Pick"),
            dataField: "ParcelSuites.length",
            sort: true
        },
        {
            dataField: "",
            text: t("Actions"),
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {["Scan Suite"].map((action) => (
                                <Dropdown.Item className="py-0" onClick={() => handleAction(action, row)}>
                                    {t(action)}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        }
    ]

    const expandRow = {
        renderer: (row: Record<string, any>) => {
            if (row.ParcelSuites.length <= 0)
                return (
                    <Row className="expanding-row px-4 py-4">
                        <Col xs={12} sm={12} md={12} lg={5}>
                            {t("No_Record")}
                        </Col>
                    </Row>
                )

            const columns = [
                {
                    dataField: "created_at",
                    text: t("Date"),
                    sort: true,
                    formatter: (cell: number, row: Record<string, any>) => {
                        if (cell) return moment(cell).format("DD MMM yyyy")
                    }
                },
                {
                    dataField: "parcel.uuid",
                    text: t("Parcel ID"),
                    classes: "highlighted-text"
                },
                {
                    text: t("AWB"),
                    dataField: "parcel.awb"
                },
                {
                    text: t("Customer"),
                    dataField: "user.name",
                    formatter: (cell: string | number, row: Record<string, any>) => {
                        return (
                            <div>
                                <span className="d-flex">{row.user.user_pin}</span>
                                <span>{cell}</span>
                            </div>
                        )
                    },
                    csvFormatter: (cell: string | number, row: Record<string, any>) => {
                        return row.user.user_pin + " ," + cell
                    }
                },
                {
                    text: t("Destination"),
                    dataField: "parcel.ParcelAddress.country"
                },
                {
                    text: t("NO_OF_ITEMS"),
                    dataField: "parcel.ParcelHasItems.length"
                },
                {
                    dataField: "parcel.weight_value",
                    text: t("Actual_Weight"),
                    formatter: (cell: string | number, row: Record<string, any>) => {
                        return cell ? (
                            <div>
                                <strong>{cell}</strong> {row.parcel?.weight_unit}
                            </div>
                        ) : (
                            <div>
                                <strong>{0}</strong>
                            </div>
                        )
                    }
                },
                {
                    dataField: "parcel.weight_value",
                    text: t("Chargeable weight"),
                    formatter: (cell: string | number, row: Record<string, any>) => {
                        const actualWeight = row.parcel?.weight_value
                        const VolumeWeight = GetVolumeWeight(row.parcel)
                        const chargeableWeight = ChargeableWeight(VolumeWeight, actualWeight)
                        return chargeableWeight ? (
                            <div>
                                <strong>{chargeableWeight.toFixed(2)}</strong> {row.parcel?.weight_unit}
                            </div>
                        ) : (
                            <div>
                                <strong>{0}</strong>
                            </div>
                        )
                    },
                    csvFormatter: (cell: string | number, row: Record<string, any>) => {
                        const actualWeight = row.parcel?.weight_value
                        const VolumeWeight = GetVolumeWeight(row.parcel)
                        const chargeableWeight = ChargeableWeight(VolumeWeight, actualWeight)
                        return chargeableWeight.toFixed(2)
                    }
                },
                {
                    dataField: "parcel.dimension_length",
                    text: t("Dimensions"),
                    formatter: (cell: string | number, row: Record<string, any>) => {
                        const w = row.parcel?.dimension_width
                        const h = row.parcel?.dimension_height

                        return cell ? cell + " x " + w + " x " + h + "  " + row.parcel?.dimensions_unit : "-"
                    }
                },
                {
                    dataField: "parcel.amount",
                    text: t("Fees"),
                    formatter: (cell: string | number, row: Record<string, any>) => {
                        return (
                            <div>
                                <strong>{cell}</strong> {row.parcel?.currency}
                            </div>
                        )
                    },
                    csvFormatter: (cell: string | number, row: Record<string, any>) => {
                        return cell + " " + row.parcel?.currency
                    }
                },
                {
                    text: t("Status"),
                    dataField: "parcel.ParcelHasParcelStatus.ParcelStatus.name",
                    formatter: (cell: string, row: Record<string, any>) => {
                        return cell ? (
                            <div
                                className={cell
                                    .toLowerCase()
                                    .replace(/[^a-zA-Z ]/g, " ")
                                    .split(" ")
                                    .join("-")}>
                                {cell}
                            </div>
                        ) : (
                            ""
                        )
                    }
                },
                {
                    text: t("Shipping"),
                    dataField: "parcel.ShippingCompany.shipping_name",
                    style: {
                        width: 150
                    },
                    formatter: (cell: string | number, row: Record<string, any>) => {
                        if (cell)
                            return (
                                <img
                                    width="100"
                                    height="80"
                                    src={"" + row.parcel?.ShippingCompany.logo}
                                    alt="shipping_company"
                                />
                            )
                    }
                }
            ]
            return (
                <BootstrapTable
                    keyField="id"
                    data={row.ParcelSuites}
                    columns={columns as ColumnDescription<any, any>[]}
                    wrapperClasses="inner-table my-3 mx-4 "
                    headerClasses="header-class"
                    bordered={false}
                    noDataIndication={t("No_Record")}
                    rowClasses="inner-table-row"
                    condensed={true}
                />
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {Columns && (
                <ToolkitProvider
                    keyField="id"
                    data={picks}
                    columns={Columns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "PickTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search mx-2 justify-content-end">
                                <Col xs={5} sm={4} md={3} lg={2} className="search-width">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                <Col className="d-none">
                                    <ExportCSVButton {...props.csvProps}>Export</ExportCSVButton>
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        // expandRow={expandRow}
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={filterFactory()}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default PickSuite
